// TT-287: remember which campaign brought a customer in, first touch, until sign up.
// Storage is a parameter so tests run without a browser; defaults to localStorage.

import { hasTargetingConsent } from './consent';

export const ATTRIBUTION_STORAGE_KEY = 'pf-meta-attribution';

const DAY_MS = 24 * 60 * 60 * 1000;
const MAX_AGE_MS = 30 * DAY_MS;
// TT-289: once sign up has created an event_id, keep it until a late email confirm
const SIGNED_UP_MAX_AGE_MS = 60 * DAY_MS;
const MAX_LENGTH = 255;

const UTM_FIELDS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_id'] as const;
const CAMPAIGN_FIELDS = [...UTM_FIELDS, 'fbclid'] as const;

type CampaignField = (typeof CAMPAIGN_FIELDS)[number];

type StoredAttribution = Partial<Record<CampaignField | 'fbc', string>> & {
  landing_path: string;
  captured_at: number;
  event_id?: string;
};

export type MetaAttributionPayload = Partial<Record<CampaignField | 'fbc' | 'fbp', string>> & {
  event_id: string;
  event_source_url: string;
};

type KeyValueStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function browserStorage(): KeyValueStorage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function clean(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim().slice(0, MAX_LENGTH);
  return trimmed || undefined;
}

function read(storage: KeyValueStorage | null, now: number): StoredAttribution | null {
  try {
    const raw = storage?.getItem(ATTRIBUTION_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as StoredAttribution;
    const maxAge = parsed?.event_id ? SIGNED_UP_MAX_AGE_MS : MAX_AGE_MS;
    if (typeof parsed?.captured_at !== 'number' || now - parsed.captured_at > maxAge) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function write(storage: KeyValueStorage | null, value: StoredAttribution): void {
  try {
    storage?.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(value));
  } catch {
    // ignore unavailable / quota-exceeded storage
  }
}

function readCookie(cookies: string, name: string): string | undefined {
  const match = cookies.split(';').find((part) => part.trim().startsWith(`${name}=`));
  return clean(match?.trim().slice(name.length + 1));
}

function uuid4(): string {
  if (typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // older browsers: RFC 4122 v4 from getRandomValues (version nibble 4, variant 8 to b)
  const hex = Array.from(crypto.getRandomValues(new Uint8Array(32)), (b) => (b % 16).toString(16));
  hex[12] = '4';
  hex[16] = ['8', '9', 'a', 'b'][Number.parseInt(hex[16] ?? '0', 16) % 4] ?? '8';
  const id = hex.join('');
  return `${id.slice(0, 8)}-${id.slice(8, 12)}-${id.slice(12, 16)}-${id.slice(16, 20)}-${id.slice(20)}`;
}

// TT-287 per Legal (5 October 2026): a campaign seen before targeting consent is held
// in memory only, and reaches the device only if the visitor says yes on this page.
let pendingCampaign: StoredAttribution | null = null;

/**
 * Store campaign fields from the landing URL. The first campaign wins for 30 days,
 * or 60 days once sign up has created its event_id. Without consent nothing is stored.
 */
export function captureAttribution(
  search: string,
  pathname: string,
  now: number = Date.now(),
  storage: KeyValueStorage | null = browserStorage(),
  consented: boolean = hasTargetingConsent()
): void {
  const params = new URLSearchParams(search);
  const fields: Partial<Record<CampaignField, string>> = Object.fromEntries(
    CAMPAIGN_FIELDS.map((field) => [field, clean(params.get(field))]).filter(([, value]) => value)
  );
  if (Object.keys(fields).length === 0) {
    return;
  }

  const campaign: StoredAttribution = {
    ...fields,
    ...(fields.fbclid ? { fbc: `fb.1.${now}.${fields.fbclid}` } : {}),
    landing_path: pathname,
    captured_at: now,
  };
  if (!consented) {
    pendingCampaign = campaign;
    return;
  }
  if (!read(storage, now)) {
    write(storage, campaign);
  }
}

/** On a targeting "yes", store the campaign held since landing. Never throws. */
export function commitPendingAttribution(
  now: number = Date.now(),
  storage: KeyValueStorage | null = browserStorage()
): void {
  const campaign = pendingCampaign;
  pendingCampaign = null;
  if (campaign && !read(storage, now)) {
    write(storage, campaign);
  }
}

/**
 * Attribution to send with sign up, or null when no campaign is stored or the
 * visitor has not given Targeting consent (TT-291). Creates the shared event_id
 * once and reuses it on every retry.
 */
export function getAttributionForSignUp(
  cookies: string,
  pageUrl: string,
  now: number = Date.now(),
  storage: KeyValueStorage | null = browserStorage()
): MetaAttributionPayload | null {
  // runs inside the sign up submit: never throw
  try {
    if (!hasTargetingConsent(undefined, cookies)) {
      return null;
    }
    const stored = read(storage, now);
    if (!stored) {
      return null;
    }
    if (!stored.event_id) {
      stored.event_id = uuid4();
      write(storage, stored);
    }

    const url = new URL(pageUrl);
    return {
      event_id: stored.event_id,
      ...Object.fromEntries(CAMPAIGN_FIELDS.filter((field) => stored[field]).map((field) => [field, stored[field]])),
      fbc: readCookie(cookies, '_fbc') ?? stored.fbc,
      fbp: readCookie(cookies, '_fbp'),
      event_source_url: `${url.origin}${url.pathname}`,
    };
  } catch {
    return null;
  }
}

export type SignUpConfirmAttribution = {
  eventId: string | null;
  hasCampaign: boolean;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
};

/**
 * TT-289: what the confirm step needs: the identifier created at sign up (read for
 * 60 days once set) and the campaign names for product analytics. Never throws.
 */
export function getAttributionForSignUpConfirm(
  now: number = Date.now(),
  storage: KeyValueStorage | null = browserStorage()
): SignUpConfirmAttribution {
  const stored = read(storage, now);
  if (!stored) {
    return { eventId: null, hasCampaign: false };
  }
  return {
    eventId: stored.event_id ?? null,
    hasCampaign: true,
    ...(stored.utm_source ? { utm_source: stored.utm_source } : {}),
    ...(stored.utm_medium ? { utm_medium: stored.utm_medium } : {}),
    ...(stored.utm_campaign ? { utm_campaign: stored.utm_campaign } : {}),
  };
}

/**
 * Remove the stored campaign once it has done its job (TT-289) or on a consent "no" (TT-291).
 */
export function clearAttribution(storage: KeyValueStorage | null = browserStorage()): void {
  pendingCampaign = null;
  try {
    storage?.removeItem(ATTRIBUTION_STORAGE_KEY);
  } catch {
    // ignore unavailable storage
  }
}
